import type { ElementType, ComponentPropsWithoutRef, ReactNode } from 'react'
import { cn } from '../../lib/cn'

const variantStyles = {
  display: 'text-4xl font-bold tracking-tight',
  h1: 'text-3xl font-bold tracking-tight',
  h2: 'text-2xl font-semibold tracking-tight',
  body: 'text-base font-normal',
  'body-sm': 'text-sm font-normal',
  caption: 'text-xs font-normal text-foreground/60',
  label: 'text-sm font-medium',
} as const

type TextVariant = keyof typeof variantStyles

type TextProps<T extends ElementType = 'p'> = {
  as?: T
  variant?: TextVariant
  className?: string
  children?: ReactNode
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'variant' | 'className' | 'children'>

export function Text<T extends ElementType = 'p'>({
  as,
  variant = 'body',
  className,
  children,
  ...props
}: TextProps<T>) {
  const Component = as ?? defaultElement[variant]
  return (
    <Component className={cn('font-sans text-foreground', variantStyles[variant], className)} {...props}>
      {children}
    </Component>
  )
}

const defaultElement: Record<TextVariant, ElementType> = {
  display: 'h1',
  h1: 'h1',
  h2: 'h2',
  body: 'p',
  'body-sm': 'p',
  caption: 'span',
  label: 'label',
}
